import ApiService from '@/common/api.service'

import * as utils from '@/common/utils'
import * as unitsHelper from './unitsHelper'

const module = 'units/cfgTemplatesModule'

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return unitsHelper.properties('template')
}

function formDefaults () {
  return {
    unit_types: [],
    unit_statuses: [],
    unit_models: [],
    sensorData: []
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: unitsHelper.properties('template'),
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  }

}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    return ApiService
      .get(`units/templates/${id}`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request "create" form data from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchFormCreate (context) {
    return ApiService
      .get(`units/templates/create/`)
      .then((response) => {
        context.commit('set', defaults())
        context.commit('setFormData', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request a record and "edit" form data from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchFormEdit (context, id) {
    return ApiService
      .get(`units/templates/${id}/edit/`)
      .then((response) => {
        const cfgTemplate = unitsHelper.transformLoad(response.data.data, 'template')

        context.commit('set', cfgTemplate)
        context.commit('setOriginal', cfgTemplate)
        context.commit('setFormData', response.data.data)
        context.commit('setIsModified', false)

        return response
      })
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    return ApiService
      .post(`units/templates`, unitsHelper.transformCreate(context.getters['get'], 'template'))
      .then((response) => {
        context.commit('set', defaults())
        context.commit('setIsModified', false)
        utils.addRecentHistory(module, unitsHelper.transformLoad(response.data.data, 'template'))
        return response
      })
  },

  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  put (context) {
    const cfgTemplate = context.getters['get']

    return ApiService
      .put(`units/templates/${cfgTemplate.id}`, unitsHelper.transformUpdate(cfgTemplate, 'template'))
      .then((response) => {
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * DELETE existing record through the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  delete (context) {
    const id = context.getters['get'].id
    return ApiService
      .delete(`units/templates/${id}`)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`units/templates`, params)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = Object.assign({}, data)
  },

  setFormData (state, data) {
    state.form = {
      partners: data.partners,
      unit_types: data.unit_types,
      unit_models: data.unit_models,
      unit_statuses: data.unit_statuses,
      sensorData: data.sensor_data,
      timezones: data.timezones
    }
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original = {
      name: data.name
    }
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * removes an element from an array of a specific property
   * and replaced it with a new element.
   *
   * @param state   Object
   * @param name    String Name of property
   * @param index   Number The index of the element to remove
   * @param replacement New element to replace the old
   */
  spliceProp (state, { name, index, replacement }) {
    state.isModified = true
    state.data[name].splice(index, 1, replacement)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = Object.assign({}, defaults())
    state.original = unitsHelper.properties('template')
    state.isModified = false
    state.isLoading = false
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = Object.assign({}, formDefaults())
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/units/cfgTemplatesModule.js